import { useQuery } from '@tanstack/react-query';
import { Download, FileBarChart } from 'lucide-react';
import { useState } from 'react';
import { PERMISSIONS } from '@odr/shared';

import { PageHeader } from '../../components/AppShell';
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Select,
  Spinner,
  StatusPill,
} from '../../components/ui';
import { api, qs, tokens } from '../../lib/api';
import { useCan } from '../../lib/auth';
import { formatPercent, humanise } from '../../lib/format';

type ReportKey = 'compliance' | 'problems' | 'recurring' | 'operators' | 'opl' | 'training';

const REPORTS: Array<{ key: ReportKey; label: string; description: string }> = [
  {
    key: 'compliance',
    label: 'CILT compliance',
    description: 'Scheduled checks completed within their window, by machine, line, shift or day.',
  },
  {
    key: 'problems',
    label: 'Problem tags',
    description: 'Open, closed and overdue tags grouped by machine, category, severity or root cause.',
  },
  {
    key: 'recurring',
    label: 'Recurring problems',
    description: 'Components tagged more than once — the AM Step 2 candidate list.',
  },
  {
    key: 'operators',
    label: 'Operator activity',
    description: 'Runs started, submitted and verified per operator, with abnormalities found.',
  },
  { key: 'opl', label: 'One-Point Lessons', description: 'Lesson library by status, type and machine.' },
  { key: 'training', label: 'Training matrix', description: 'AM step competency and OPL progress per operator.' },
];

const GROUP_OPTIONS: Partial<Record<ReportKey, Array<{ value: string; label: string }>>> = {
  compliance: [
    { value: 'machine', label: 'By machine' },
    { value: 'line', label: 'By line' },
    { value: 'template', label: 'By checklist' },
    { value: 'shift', label: 'By shift' },
    { value: 'day', label: 'By day' },
  ],
  problems: [
    { value: 'machine', label: 'By machine' },
    { value: 'category', label: 'By category' },
    { value: 'severity', label: 'By severity' },
    { value: 'rootCause', label: 'By root cause' },
    { value: 'status', label: 'By status' },
    { value: 'line', label: 'By line' },
  ],
};

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
}

export function ReportsPage() {
  const canExport = useCan(PERMISSIONS.REPORT_EXPORT);
  const [report, setReport] = useState<ReportKey>('compliance');
  const [groupBy, setGroupBy] = useState('machine');
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));

  const query = qs({
    from: `${from}T00:00:00.000Z`,
    to: `${to}T23:59:59.999Z`,
    groupBy: GROUP_OPTIONS[report] ? groupBy : undefined,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['report', report, query],
    queryFn: () => api.get<unknown>(`/api/reports/${report}${query}`),
  });

  const download = async () => {
    // Fetch with the bearer token, then save the blob — a plain link cannot
    // carry the Authorization header.
    const response = await fetch(`/api/reports/${report}${query}&format=csv`, {
      headers: { Authorization: `Bearer ${tokens.access() ?? ''}` },
    });
    if (!response.ok) return;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `odr-${report}-${from}-to-${to}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const active = REPORTS.find((r) => r.key === report)!;

  return (
    <div>
      <PageHeader
        title="Reports"
        subtitle="Everything here is calculated from ODR's own records"
        actions={
          canExport ? (
            <Button variant="secondary" icon={Download} onClick={() => void download()}>
              Export CSV
            </Button>
          ) : null
        }
      />

      <div className="space-y-4 p-4 lg:p-6">
        <Card className="p-3">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Report">
              <Select
                value={report}
                onChange={(event) => {
                  const next = event.target.value as ReportKey;
                  setReport(next);
                  setGroupBy(GROUP_OPTIONS[next]?.[0]?.value ?? 'machine');
                }}
              >
                {REPORTS.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>

            {GROUP_OPTIONS[report] ? (
              <Field label="Group by">
                <Select value={groupBy} onChange={(event) => setGroupBy(event.target.value)}>
                  {GROUP_OPTIONS[report]!.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}

            <Field label="From">
              <Input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
            </Field>
            <Field label="To">
              <Input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
            </Field>
          </div>
          <p className="mt-2 px-1 text-xs text-steel-500">{active.description}</p>
        </Card>

        <Card>
          <CardHeader title={active.label} />
          {isLoading ? (
            <Spinner label="Running report" />
          ) : error ? (
            <EmptyState
              icon={FileBarChart}
              title="Could not run this report"
              message={(error as Error).message}
            />
          ) : (
            <ReportTable report={report} data={data} />
          )}
        </Card>
      </div>
    </div>
  );
}

function ReportTable({ report, data }: { report: ReportKey; data: unknown }) {
  if (report === 'compliance') {
    const typed = data as {
      data: Array<{ key: string; label: string; expected: number; done: number; missed: number; overdue: number; rate: number | null }>;
      totals: { expected: number; done: number; missed: number; rate: number | null };
    };
    if (typed.data.length === 0) return <Empty />;
    return (
      <Table
        head={['', 'Expected', 'Done', 'Missed', 'Overdue', 'Compliance']}
        rows={typed.data.map((row) => [
          row.label,
          row.expected,
          row.done,
          row.missed,
          row.overdue,
          <RateCell key="rate" rate={row.rate} />,
        ])}
        foot={[
          'Total',
          typed.totals.expected,
          typed.totals.done,
          typed.totals.missed,
          '',
          <RateCell key="total" rate={typed.totals.rate} />,
        ]}
      />
    );
  }

  if (report === 'problems') {
    const typed = data as {
      data: Array<{ label: string; total: number; open: number; closed: number; critical: number; overdue: number; avgDaysToClose: number | null }>;
    };
    if (typed.data.length === 0) return <Empty />;
    return (
      <Table
        head={['', 'Total', 'Open', 'Closed', 'Critical', 'Overdue', 'Avg days to close']}
        rows={typed.data.map((row) => [
          humanise(row.label),
          row.total,
          row.open,
          row.closed,
          row.critical,
          row.overdue,
          row.avgDaysToClose ?? '—',
        ])}
      />
    );
  }

  if (report === 'recurring') {
    const typed = data as {
      data: Array<{ machineCode: string; componentName: string; occurrences: number; openNow: number; hasOpl: number }>;
    };
    if (typed.data.length === 0) return <Empty />;
    return (
      <Table
        head={['Machine', 'Component', 'Occurrences', 'Open now', 'Has an OPL']}
        rows={typed.data.map((row) => [
          row.machineCode,
          row.componentName,
          row.occurrences,
          row.openNow,
          <StatusPill
            key="opl"
            tone={row.hasOpl > 0 ? 'ok' : 'warn'}
            label={row.hasOpl > 0 ? 'Yes' : 'Not yet'}
          />,
        ])}
      />
    );
  }

  if (report === 'operators') {
    const typed = data as {
      data: Array<{ operatorName: string; shift: string | null; runsStarted: number; runsSubmitted: number; runsVerified: number; abnormalities: number; problemsRaised: number }>;
    };
    if (typed.data.length === 0) return <Empty />;
    return (
      <Table
        head={['Operator', 'Shift', 'Started', 'Submitted', 'Verified', 'Abnormalities', 'Tags raised']}
        rows={typed.data.map((row) => [
          row.operatorName,
          row.shift ?? '—',
          row.runsStarted,
          row.runsSubmitted,
          row.runsVerified,
          row.abnormalities,
          row.problemsRaised,
        ])}
      />
    );
  }

  if (report === 'opl') {
    const typed = data as {
      detail: Array<{ oplNo: string; title: string; oplType: string; status: string; machineCode: string | null; trainedCount: number; assignedCount: number }>;
    };
    if (typed.detail.length === 0) return <Empty />;
    return (
      <Table
        head={['OPL', 'Title', 'Type', 'Machine', 'Status', 'Competent / assigned']}
        rows={typed.detail.map((row) => [
          row.oplNo,
          row.title,
          humanise(row.oplType),
          row.machineCode ?? '—',
          humanise(row.status),
          `${row.trainedCount} / ${row.assignedCount}`,
        ])}
      />
    );
  }

  const typed = data as {
    steps: Array<{ stepNo: number; name: string; inPilot: boolean }>;
    operators: Array<{
      fullName: string;
      shift: string | null;
      steps: Array<{ stepNo: number; level: string }>;
      oplsCompetent: number;
      oplsAssigned: number;
    }>;
  };
  if (typed.operators.length === 0) return <Empty />;
  return (
    <Table
      head={[
        'Operator',
        'Shift',
        ...typed.steps.map((s) => `Step ${s.stepNo}`),
        'OPLs competent',
      ]}
      rows={typed.operators.map((operator) => [
        operator.fullName,
        operator.shift ?? '—',
        ...operator.steps.map((step) => humanise(step.level)),
        `${operator.oplsCompetent} / ${operator.oplsAssigned}`,
      ])}
    />
  );
}

function RateCell({ rate }: { rate: number | null }) {
  return (
    <StatusPill
      tone={rate === null ? 'neutral' : rate >= 95 ? 'ok' : rate >= 80 ? 'warn' : 'alarm'}
      label={formatPercent(rate)}
    />
  );
}

function Empty() {
  return (
    <EmptyState
      icon={FileBarChart}
      title="No data in this range"
      message="Nothing was recorded between those dates. Try widening the date range."
    />
  );
}

function Table({
  head,
  rows,
  foot,
}: {
  head: React.ReactNode[];
  rows: React.ReactNode[][];
  foot?: React.ReactNode[];
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[600px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-steel-200 bg-steel-50">
            {head.map((cell, index) => (
              <th
                key={index}
                scope="col"
                className={`px-4 py-2 font-bold text-steel-700 ${index === 0 ? 'text-left' : 'text-right'}`}
              >
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="border-b border-steel-100">
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={`px-4 py-2.5 ${cellIndex === 0 ? 'text-left font-medium text-steel-900' : 'text-right tabular-nums text-steel-700'}`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {foot ? (
          <tfoot>
            <tr className="border-t-2 border-steel-300 bg-steel-50 font-bold">
              {foot.map((cell, index) => (
                <td
                  key={index}
                  className={`px-4 py-2.5 ${index === 0 ? 'text-left' : 'text-right tabular-nums'}`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}
